import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { ADMIN_EMAILS } from '../lib/adminEmails'

interface AdminUser {
  id: string
  user_id: string
  role: 'admin' | 'super_admin'
  permissions: string[]
  created_at: string
  is_active: boolean
}

interface AdminAuthContextType {
  isAdmin: boolean
  adminUser: AdminUser | null
  adminLoading: boolean
  checkAdminStatus: () => Promise<boolean>
  adminSignOut: () => Promise<void>
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut, loading } = useAuth()
  
  // Initialize with localStorage data if available
  const getInitialAdminState = () => {
    if (typeof window === 'undefined') return { isAdmin: false, adminUser: null }
    
    try {
      // Test localStorage availability
      const testKey = '__localStorage_test__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      
      const storedAdminStatus = localStorage.getItem('adminStatus')
      
      if (storedAdminStatus) {
        const parsed = JSON.parse(storedAdminStatus)
        // We'll verify this matches the current user later
        return {
          isAdmin: parsed.isAdmin || false,
          adminUser: parsed.adminUser || null
        }
      }
    } catch (error) {
      console.error('❌ Error reading stored admin status:', error)
      try {
        localStorage.removeItem('adminStatus')
      } catch (removeError) {
        console.error('❌ Cannot remove from localStorage:', removeError)
      }
    }
    
    return { isAdmin: false, adminUser: null }
  }
  
  const initialState = getInitialAdminState()
  const [isAdmin, setIsAdmin] = useState(initialState.isAdmin)
  const [adminUser, setAdminUser] = useState<AdminUser | null>(initialState.adminUser)
  const [adminLoading, setAdminLoading] = useState(true)

  // Check if email is a known admin email
  const isKnownAdminEmail = useCallback((email: string | undefined): boolean => {
    return email ? ADMIN_EMAILS.includes(email.toLowerCase()) : false
  }, [])

  const checkAdminStatus = useCallback(async (): Promise<boolean> => {
    if (!user) {
      setIsAdmin(false)
      setAdminUser(null)
      setAdminLoading(false)
      // Clear stored admin status
      localStorage.removeItem('adminStatus')
      return false
    }

    try {
      // For known admin emails, grant immediate access
      if (isKnownAdminEmail(user.email || undefined)) {
        const cassieAdminUser: AdminUser = {
          id: user.id,
          user_id: user.id,
          role: 'super_admin',
          permissions: ['all'],
          created_at: new Date().toISOString(),
          is_active: true
        }
        
        setIsAdmin(true)
        setAdminUser(cassieAdminUser)
        setAdminLoading(false)
        
        // Store admin status
        try {
          localStorage.setItem('adminStatus', JSON.stringify({
            isAdmin: true,
            adminUser: cassieAdminUser,
            userId: user.id
          }))
        } catch (storageError) {
          console.warn('Could not store admin status:', storageError)
        }
        
        return true
      }

      // For non-admin emails, skip database check to avoid errors
      // This eliminates the 500 errors for regular users
      console.log('User is not a known admin, skipping database check')
      setIsAdmin(false)
      setAdminUser(null)
      setAdminLoading(false)
      
      // Clear stored admin status
      try {
        localStorage.removeItem('adminStatus')
      } catch (storageError) {
        console.warn('Could not clear admin status:', storageError)
      }
      
      return false
    } catch (error) {
      console.error('Error checking admin status:', error)
      setIsAdmin(false)
      setAdminUser(null)
      setAdminLoading(false)
      return false
    }
  }, [user, isKnownAdminEmail])

  // Verify and maintain admin status when user changes
  useEffect(() => {
    
    // Don't do anything while auth is still loading
    if (loading) {
      return
    }
    
    if (!user) {
      setIsAdmin(false)
      setAdminUser(null)
      setAdminLoading(false)
      localStorage.removeItem('adminStatus')
      return
    }

    // Check if we have stored admin status for this user
    const storedAdminStatus = localStorage.getItem('adminStatus')
    
    if (storedAdminStatus) {
      try {
        const parsed = JSON.parse(storedAdminStatus)
        
        // If stored data matches current user, we're good
        if (parsed.userId === user.id && parsed.isAdmin) {
          setIsAdmin(true)
          setAdminUser(parsed.adminUser)
          setAdminLoading(false)
          return
        } else {
          localStorage.removeItem('adminStatus')
        }
      } catch (error) {
        console.error('Error parsing stored admin status:', error)
        localStorage.removeItem('adminStatus')
      }
    }
    
    // No valid stored status, perform fresh check
    checkAdminStatus()
  }, [user, loading, checkAdminStatus])

  const adminSignOut = async () => {
    try {
      setIsAdmin(false)
      setAdminUser(null)
      localStorage.removeItem('adminStatus')
      await signOut()
    } catch (error) {
      console.error('Admin sign out error:', error)
      throw error
    }
  }

  const value = {
    isAdmin,
    adminUser,
    adminLoading,
    checkAdminStatus,
    adminSignOut,
  }

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
} 